import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  AlertTriangleIcon,
  FlagIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  TrophyIcon,
  TargetIcon,
  LightbulbIcon,
  SparklesIcon,
  MinusIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  UsersIcon,
  BarChart3Icon,
  CheckIcon,
  HelpCircleIcon,
  XIcon,
  Maximize2Icon,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { tenantApi } from '../lib/tenant-api';
import type {
  AIAuthorityDataFull,
  AIAuthorityPlatformAnalysis,
  AIAuthorityFlaggedAnswer,
  AIAuthorityRubricScore,
  AIAuthorityReportItem,
} from '../lib/api-types';
import PageLoader from '../components/common/PageLoader';
import { PlatformIcon } from '../components/AIAuthority';
import ReportSelector from '../components/aeo/ReportSelector';
import CompetitiveLandscape from '../components/aeo/CompetitiveLandscape';
import MarkdownRenderer from '../components/MarkdownRenderer';
import DashboardAssistantBar from '../components/DashboardAssistantBar';
import AIInsightsDrawer from '../components/AIInsightsDrawer';
import { useAeoReport } from '../contexts/AeoReportContext';

// ── Helpers ──────────────────────────────────────────────────────────

const clamp = (n?: number) => Math.max(0, Math.min(100, n ?? 0));

function fmt(n?: number | null, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return (Math.round(n * 10 ** digits) / 10 ** digits).toString();
}

// Rubric scores arrive as a bare number (old reports) or { mean, count } (new).
function rubricMean(v?: AIAuthorityRubricScore): number | undefined {
  if (v === null || v === undefined) return undefined;
  const n = typeof v === 'number' ? v : v.mean;
  return n === null || n === undefined || Number.isNaN(n) ? undefined : n;
}

function scoreTone(score?: number) {
  if (score === null || score === undefined) return { text: 'text-gray-500', bar: 'bg-gray-300' };
  if (score >= 80) return { text: 'text-emerald-700', bar: 'bg-emerald-500' };
  if (score >= 60) return { text: 'text-blue-700', bar: 'bg-blue-500' };
  if (score >= 40) return { text: 'text-amber-700', bar: 'bg-amber-500' };
  return { text: 'text-red-700', bar: 'bg-red-500' };
}

function scoreLabel(s?: number): string {
  if (s === null || s === undefined) return 'No data';
  if (s >= 80) return 'Excellent';
  if (s >= 60) return 'Good';
  if (s >= 40) return 'Fair';
  return 'Needs work';
}

function priorityMeta(p?: string) {
  const s = (p || '').toLowerCase();
  if (s.includes('crit')) return { rank: 0, badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('high')) return { rank: 1, badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' };
  if (s.includes('med')) return { rank: 2, badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
  if (s.includes('low')) return { rank: 3, badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' };
  return { rank: 4, badge: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
}

// Question performance sort options.
type QuestionSort = 'lowest' | 'highest' | 'gap' | 'az';
const QUESTION_SORTS: { value: QuestionSort; label: string }[] = [
  { value: 'highest', label: 'Strongest first' },
  { value: 'lowest', label: 'Weakest first' },
  { value: 'gap', label: 'Biggest gap to best' },
  { value: 'az', label: 'Question A–Z' },
];

// Score-distribution buckets (0–20 … 80–100).
const DIST_LABELS = ['0–20', '20–40', '40–60', '60–80', '80–100'];
const DIST_COLORS = ['bg-red-500', 'bg-amber-500', 'bg-amber-400', 'bg-blue-500', 'bg-emerald-500'];

// Distinct colors for the "other platforms" on the rubric radar (current = navy).
const RADAR_COLORS = ['#0ea5e9', '#f59e0b', '#10b981', '#e11d48', '#8b5cf6', '#64748b', '#14b8a6', '#d946ef'];

// Full-report platform_analysis keys are provider/model names (e.g. "openai",
// "google", "anthropic"); AI Authority uses product names ("ChatGPT", "Gemini",
// "Claude"). Bridge them so the leaderboard shows the same labels + icons.
const PLATFORM_ALIASES: Record<string, string> = {
  openai: 'ChatGPT',
  chatgpt: 'ChatGPT',
  gpt: 'ChatGPT',
  anthropic: 'Claude',
  claude: 'Claude',
  google: 'Gemini',
  gemini: 'Gemini',
  'google ai': 'Google AI',
  xai: 'Grok',
  grok: 'Grok',
  perplexity: 'Perplexity',
  microsoft: 'Copilot',
  copilot: 'Copilot',
  deepseek: 'DeepSeek',
  meta: 'Meta AI',
  'meta ai': 'Meta AI',
  llama: 'Meta AI',
};

// Canonical id that collapses product names (ChatGPT) and provider/model keys
// (openai) onto one token, so the two naming systems can be matched.
const PLATFORM_ID: Record<string, string> = {
  chatgpt: 'openai', openai: 'openai', gpt: 'openai',
  claude: 'anthropic', anthropic: 'anthropic',
  gemini: 'google', google: 'google', 'google ai': 'google',
  grok: 'grok', xai: 'grok',
  perplexity: 'perplexity',
  copilot: 'copilot', microsoft: 'copilot',
  deepseek: 'deepseek',
  'meta ai': 'meta', meta: 'meta', llama: 'meta',
};
function platformId(name?: string): string {
  const l = (name ?? '').trim().toLowerCase();
  return PLATFORM_ID[l] ?? l;
}
// Matches provider/model or product names embedded in free text (longest first).
const NAME_TOKEN_RE = /\b(google ai|meta ai|openai|anthropic|google|gemini|chatgpt|claude|grok|xai|perplexity|microsoft|copilot|deepseek|meta|llama|gpt)\b/gi;

const RUBRICS: { key: keyof NonNullable<AIAuthorityPlatformAnalysis['rubric_averages']>; label: string }[] = [
  { key: 'visibility', label: 'Visibility' },
  { key: 'authority', label: 'Authority' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'competitive_share', label: 'Competitive Share' },
  { key: 'sentiment', label: 'Sentiment' },
];

// ── Sub-components ───────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, icon: Icon, right, children }) => (
  <section className="border border-gray-100 bg-white  p-5 sm:p-6">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3 mb-4">
      <div className="flex items-center gap-2.5 min-w-0">
        {Icon && (
          <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-[#161463]" />
          </div>
        )}
        <div>
          <h2 className="text-base font-bold text-[#05004E]">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
    {children}
  </section>
);

const MiniStat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div>
    <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
    {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
  </div>
);

const ScoreBar: React.FC<{ score?: number; benchmark?: number }> = ({ score, benchmark }) => {
  const tone = scoreTone(score);
  return (
    <div className="relative w-full bg-gray-100 rounded-full h-2.5">
      <div className={`h-2.5 rounded-full ${tone.bar} transition-all`} style={{ width: `${clamp(score)}%` }} />
      {benchmark !== undefined && (
        <div
          className="absolute top-1/2 -translate-y-1/2 h-4 w-0.5 bg-gray-500/70 rounded"
          style={{ left: `${clamp(benchmark)}%` }}
          title={`Overall average ${fmt(benchmark)}`}
        />
      )}
    </div>
  );
};

// One labelled score bar in the brand-vs-competitor comparison rows.
const CompareRow: React.FC<{ label: string; score?: number; highlight?: boolean }> = ({ label, score, highlight }) => (
  <div className="flex items-center gap-2">
    <span className={`w-28 sm:w-40 truncate text-xs ${highlight ? 'font-semibold text-[#161463]' : 'text-gray-600'}`}>{label}</span>
    <div className="flex-1"><ScoreBar score={score} /></div>
    <span className={`w-9 text-right text-xs font-semibold ${scoreTone(score).text}`}>{fmt(score)}</span>
  </div>
);

const DeltaBadge: React.FC<{ delta?: number; suffix?: string }> = ({ delta, suffix = '' }) => {
  if (delta === undefined) return null;
  const r = Math.round(delta * 10) / 10;
  if (r === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-gray-400">
        <MinusIcon className="h-3 w-3" />0{suffix}
      </span>
    );
  }
  const up = r > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <TrendingUpIcon className="h-3 w-3" /> : <TrendingDownIcon className="h-3 w-3" />}
      {up ? '+' : ''}{r}{suffix}
    </span>
  );
};

// Collapsible answer body — renders markdown, truncates long answers with See more.
const AnswerBody: React.FC<{ text?: string; limit?: number }> = ({ text, limit = 220 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const isLong = text.length > limit;
  const shown = expanded || !isLong ? text : `${text.slice(0, limit)}…`;
  return (
    <div className="mt-3">
      <div className={expanded ? 'max-h-72 overflow-y-auto pr-1' : ''}>
        <MarkdownRenderer content={shown} className="text-sm text-gray-600" />
      </div>
      {isLong && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-1 text-sm font-medium text-[#161463] hover:underline">
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
};

const FlaggedAnswerCard: React.FC<{ answer: AIAuthorityFlaggedAnswer; delta?: number }> = ({ answer, delta }) => {
  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">{answer.question_text || answer.question_id || 'Question'}</p>
        <div className="flex-shrink-0 flex items-center gap-2">
          {answer.total_score !== undefined && (
            <span className={`text-sm font-bold ${scoreTone(answer.total_score).text}`}>{fmt(answer.total_score)}</span>
          )}
          <DeltaBadge delta={delta} />
        </div>
      </div>
      {(answer.flag_reasons?.length ?? 0) > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {answer.flag_reasons!.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white text-amber-700 text-xs border border-amber-200">
              <FlagIcon className="h-3 w-3" />{r}
            </span>
          ))}
        </div>
      )}
      {answer.scores && (
        <div className="mt-3 grid grid-cols-5 gap-1 rounded-lg bg-white/70 border border-amber-100 p-2">
          {RUBRICS.map((r) => {
            const v = rubricMean(answer.scores?.[r.key]);
            return (
              <div key={String(r.key)} className="text-center">
                <p className={`text-sm font-bold ${scoreTone(v).text}`}>{fmt(v)}</p>
                <p className="text-[10px] text-gray-400 truncate" title={r.label}>{r.label}</p>
              </div>
            );
          })}
        </div>
      )}
      <AnswerBody text={answer.answer_text} />
    </div>
  );
};

// Scrollable container that shows a fading bottom gradient + bouncing chevron
// whenever there is more content to scroll to.
const ScrollBox: React.FC<{ scrollClassName?: string; children: React.ReactNode }> = ({ scrollClassName = '', children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);

  const check = () => {
    const el = scrollRef.current;
    if (!el) return;
    setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => check());
    if (scrollRef.current) ro.observe(scrollRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="relative">
      <div ref={scrollRef} onScroll={check} className={scrollClassName}>
        <div ref={contentRef}>{children}</div>
      </div>
      <div className={`pointer-events-none absolute bottom-0 inset-x-0 h-12 flex items-end justify-center bg-gradient-to-t from-white to-transparent transition-opacity duration-200 ${more ? 'opacity-100' : 'opacity-0'}`}>
        <button
          type="button"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
          className={`mb-1 h-7 w-7 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-gray-500 hover:text-[#161463] hover:shadow-lg transition-all animate-bounce ${more ? 'pointer-events-auto' : 'pointer-events-none'}`}
        >
          <ChevronDownIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────

const PlatformDeepDive: React.FC = () => {
  const { reportId, slug } = useParams<{ reportId: string; slug: string }>();
  const navigate = useNavigate();
  const { setSelectedReportId } = useAeoReport();
  const platform = (slug || '').replace(/-deep-dive$/, '');

  const { data, isLoading, error } = useQuery<AIAuthorityDataFull>({
    queryKey: ['ai-authority-full', reportId],
    queryFn: () => tenantApi.getAIAuthorityFull(reportId!),
    enabled: !!reportId,
    retry: false,
  });

  // Report list for the dropdown (cached from the dashboard — instant if already loaded).
  const { data: listData } = useQuery({
    queryKey: ['ai-authority'],
    queryFn: () => tenantApi.getAIAuthority(),
    retry: false,
  });
  const reports = listData?.reports ?? [];

  // Primer questions — used to flag which report questions are "first class".
  const { data: primerQuestions } = useQuery({
    queryKey: ['primer-questions'],
    queryFn: () => tenantApi.getPrimerQuestions(),
    retry: false,
  });
  const currentReport = reports.find((r) => (r.full_report_id ?? r.lars_report_id) === reportId);
  const latestReportId = listData?.latest_report_id ?? reports[0]?.lars_report_id ?? null;
  const latestReport = reports.find((r) => r.lars_report_id === latestReportId) ?? reports[0];
  const isLatest = !currentReport || currentReport.lars_report_id === latestReportId;

  // Previous report (next older) — full data, for score trend deltas.
  const currentIndex = reports.findIndex((r) => (r.full_report_id ?? r.lars_report_id) === reportId);
  const prevReport = currentIndex >= 0 ? reports[currentIndex + 1] : undefined;
  const prevFullReportId = prevReport?.full_report_id ?? prevReport?.lars_report_id ?? null;
  const { data: prevFull } = useQuery({
    queryKey: ['ai-authority-full', prevFullReportId],
    queryFn: () => tenantApi.getAIAuthorityFull(prevFullReportId!),
    enabled: !!prevFullReportId,
    retry: false,
  });

  // Keep the shared selection in sync so returning to the dashboard shows this report.
  useEffect(() => {
    if (currentReport?.lars_report_id) setSelectedReportId(currentReport.lars_report_id);
  }, [currentReport?.lars_report_id, setSelectedReportId]);

  const goToReport = (r: AIAuthorityReportItem) => {
    const id = r.full_report_id ?? r.lars_report_id;
    if (!id) return;
    setSelectedReportId(r.lars_report_id ?? null);
    navigate(`/dashboard/aeo/${encodeURIComponent(id)}/${encodeURIComponent(platform)}-deep-dive`);
  };

  // Resolve the platform_analysis key. The URL carries the product name (ChatGPT)
  // while the report keys by provider (openai), so match on a canonical id.
  const matchedKey = useMemo(() => {
    const keys = Object.keys(data?.platform_analysis ?? {});
    const target = platformId(platform);
    return keys.find((k) => platformId(k) === target) ?? platform;
  }, [data, platform]);

  const pa = data?.platform_analysis?.[matchedKey];

  // Per-question scores for this platform, weakest first.
  const questionRows = useMemo(() => {
    const qa = data?.question_analysis ?? {};
    return Object.entries(qa)
      .map(([id, q]) => ({ id, q, score: q.platform_scores?.[matchedKey] }))
      .filter((r) => r.score !== undefined)
      .sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
  }, [data, matchedKey]);

  const flaggedForPlatform = useMemo(
    () => (data?.flagged_answers ?? []).filter((a) => platformId(a.platform) === platformId(platform)),
    [data, platform],
  );

  // AI Assistant (ai_authority context) — same pattern as the AI Authority dashboard.
  const [showAIChat, setShowAIChat] = useState(false);
  const [initialAIMessage, setInitialAIMessage] = useState<string | undefined>(undefined);
  const handleAssistantSubmit = (text: string) => { setInitialAIMessage(text); setShowAIChat(true); };

  // Modal to view a question and its full answer.
  const [modalQ, setModalQ] = useState<{ id: string; questionText?: string; score?: number; answer?: string } | null>(null);
  // Competitors chosen (in the modal) to compare against via the assistant.
  const [selectedCompetitors, setSelectedCompetitors] = useState<Set<string>>(new Set());
  const toggleCompetitor = (name: string) => setSelectedCompetitors((prev) => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  const [questionSort, setQuestionSort] = useState<QuestionSort>('highest');

  const backToDashboard = () => navigate('/dashboard/aeo');

  if (isLoading) return <PageLoader fullScreen={false} />;

  if (error || !data) {
    return (
      <div className="mt-6 bg-white border border-gray-100 p-10 text-center ">
        <AlertTriangleIcon className="mx-auto h-10 w-10 text-amber-500" />
        <h1 className="mt-4 text-xl font-bold text-gray-900">Deep-dive report not available</h1>
        <p className="mt-2 text-sm text-gray-500">
          We couldn't find a full report for this platform. It may not have been generated yet.
        </p>
        <button onClick={backToDashboard} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#161463] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity">
          <ArrowLeftIcon className="h-4 w-4" /> Back to AI Authority
        </button>
      </div>
    );
  }

  // ── Derived insights ──────────────────────────────────────────────
  const meta = data.metadata;

  const overallMean = typeof data.executive_summary?.overall_mean_score === 'number' ? data.executive_summary.overall_mean_score : undefined;
  const vsOverall = pa?.mean_score !== undefined && overallMean !== undefined ? pa.mean_score - overallMean : undefined;

  const platformRanking = Object.entries(data.platform_analysis ?? {})
    .map(([name, p]) => ({ name, mean: p?.mean_score }))
    .filter((e): e is { name: string; mean: number } => typeof e.mean === 'number')
    .sort((a, b) => b.mean - a.mean);
  const rank = platformRanking.findIndex((e) => e.name === matchedKey) + 1;

  const rubricRows = RUBRICS.map((r) => {
    const val = rubricMean(pa?.rubric_averages?.[r.key]);
    const bench = rubricMean(data.executive_summary?.average_scores_by_rubric?.[r.key]);
    return { ...r, val, bench, delta: val !== undefined && bench !== undefined ? val - bench : undefined };
  });
  const rated = rubricRows.filter((r) => r.val !== undefined);
  const strongest = rated.length ? rated.reduce((a, b) => ((b.val ?? 0) > (a.val ?? 0) ? b : a)) : undefined;
  const weakest = rated.length ? rated.reduce((a, b) => ((b.val ?? 0) < (a.val ?? 0) ? b : a)) : undefined;

  const belowCount = questionRows.filter((r) => (r.score ?? 0) < 60).length;

  // Resolve a platform_analysis key to the exact platform name AI Authority uses,
  // so the leaderboard shows the same labels + branded icons. Names are gathered
  // from the summary reports (platform_matrix / trust_signals / platforms_in_comparison).
  const knownPlatforms = [
    ...reports.flatMap((r) => (r.platform_matrix ?? []).map((m) => m.platform)),
    ...reports.flatMap((r) => (r.trust_signals ?? []).map((s) => s.platform)),
    ...reports.flatMap((r) => r.platforms_in_comparison ?? []),
    platform,
  ];
  const canonicalPlatform = (name: string) => {
    const lower = name.trim().toLowerCase();
    // 1. Exact (case-insensitive) match against the summary's platform names.
    const direct = knownPlatforms.find((k) => k.toLowerCase() === lower);
    if (direct) return direct;
    // 2. Map provider/model key → product name, preferring the summary's label.
    const aliased = PLATFORM_ALIASES[lower];
    if (aliased) return knownPlatforms.find((k) => k.toLowerCase() === aliased.toLowerCase()) ?? aliased;
    return name;
  };

  // Product-name label for the current platform + a mapper for names in free text.
  const platformLabel = canonicalPlatform(matchedKey);
  const mapNames = (text?: string) => (text ? text.replace(NAME_TOKEN_RE, (m) => canonicalPlatform(m)) : text);

  const recommendations = [...(data.recommendations ?? [])].sort(
    (a, b) => priorityMeta(a.priority).rank - priorityMeta(b.priority).rank,
  );
  const nextSteps = data.next_steps;

  // Radar: current platform vs every other platform, per rubric.
  // null (not 0) marks "no data" so a genuine 0 isn't confused with unmeasured.
  const radarPlatforms = Object.keys(data.platform_analysis ?? {});
  const otherRadarPlatforms = radarPlatforms.filter((k) => platformId(k) !== platformId(matchedKey));
  // Draw other platforms first so the current one sits on top.
  const orderedRadarPlatforms = [...otherRadarPlatforms, ...radarPlatforms.filter((k) => platformId(k) === platformId(matchedKey))];
  const radarColorFor = (k: string) => (platformId(k) === platformId(matchedKey) ? '#161463' : RADAR_COLORS[otherRadarPlatforms.indexOf(k) % RADAR_COLORS.length]);
  const radarData = RUBRICS.map((r) => {
    const point: Record<string, number | string | null> = { rubric: r.label };
    radarPlatforms.forEach((k) => { point[k] = rubricMean(data.platform_analysis?.[k]?.rubric_averages?.[r.key]) ?? null; });
    return point;
  });

  const renderRadarTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const rows = radarPlatforms
      .map((k) => ({ key: k, label: canonicalPlatform(k), value: d[k] as number | null, current: platformId(k) === platformId(matchedKey) }))
      .filter((row) => row.value != null)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-md px-3 py-2 text-xs min-w-[10rem]">
        <p className="font-semibold text-gray-900 mb-1">{d.rubric}</p>
        <div className="space-y-0.5">
          {rows.map((row) => (
            <div key={row.key} className={`flex items-center justify-between gap-4 ${row.current ? 'font-semibold text-[#161463]' : 'text-gray-600'}`}>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: radarColorFor(row.key) }} />{row.label}</span>
              <span className="font-semibold text-gray-900">{fmt(row.value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // #3 distribution · #2 best answers · #1 competitors · assistant suggestions
  const distribution = [0, 0, 0, 0, 0];
  questionRows.forEach((r) => { distribution[Math.min(4, Math.max(0, Math.floor((r.score ?? 0) / 20)))]++; });
  const maxBucket = Math.max(1, ...distribution);
  const bestForPlatform = (data.top_performers ?? []).filter((p) => platformId(p.platform) === platformId(platform)).slice(0, 10);
  const cc = data.competitor_comparison;

  // Per-question brand (this platform) vs competitor brands — needs backend
  // question_analysis[].competitor_scores; section stays hidden until present.
  const brandVsCompetitors = questionRows
    .map(({ id, q, score }) => {
      const comps = Object.entries(q.competitor_scores ?? {})
        .filter(([, s]) => typeof s === 'number')
        .map(([name, s]) => ({ name, score: s as number }));
      return { id, question_text: q.question_text, score, comps };
    })
    .filter((r) => r.comps.length > 0)
    .map((r) => {
      const bestComp = Math.max(...r.comps.map((c) => c.score));
      return { ...r, gap: r.score !== undefined ? r.score - bestComp : undefined };
    })
    .sort((a, b) => (a.gap ?? 0) - (b.gap ?? 0)); // biggest deficit first

  // This platform's actual answer per question. Primary source is the full per-platform
  // answers on question_analysis (new reports); performers/flagged are a fallback for
  // older reports. A present-but-empty string means the platform returned no answer.
  const answerByQuestion: Record<string, string> = {};
  [...(data.top_performers ?? []), ...(data.bottom_performers ?? [])].forEach((p) => {
    if (p.question_id && p.answer_preview && platformId(p.platform) === platformId(platform)) answerByQuestion[p.question_id] = p.answer_preview;
  });
  (data.flagged_answers ?? []).forEach((a) => {
    if (a.question_id && a.answer_text && platformId(a.platform) === platformId(platform)) answerByQuestion[a.question_id] = a.answer_text;
  });
  Object.entries(data.question_analysis ?? {}).forEach(([qid, q]) => {
    const mine = (q.answers ?? []).filter((a) => platformId(a.platform) === platformId(platform));
    if (!mine.length) return;
    const withText = mine.filter((a) => (a.answer_text ?? '').trim());
    if (withText.length) {
      answerByQuestion[qid] = withText.sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0))[0].answer_text ?? '';
    } else if (!(qid in answerByQuestion)) {
      answerByQuestion[qid] = ''; // platform has an entry but returned no text
    }
  });

  // Previous report's per-question score for this platform (trend baseline).
  const prevScoreByQuestion: Record<string, number> = {};
  Object.entries(prevFull?.question_analysis ?? {}).forEach(([qid, q]) => {
    const scores = q.platform_scores ?? {};
    const key = Object.keys(scores).find((k) => platformId(k) === platformId(platform));
    if (key !== undefined) prevScoreByQuestion[qid] = scores[key];
  });
  const questionDelta = (qid?: string, score?: number) =>
    qid && prevScoreByQuestion[qid] !== undefined && score !== undefined ? score - prevScoreByQuestion[qid] : undefined;

  // Previous report's mean score per platform (keyed by canonical platform id).
  const prevPlatformMean: Record<string, number> = {};
  Object.entries(prevFull?.platform_analysis ?? {}).forEach(([k, p]) => {
    if (typeof p?.mean_score === 'number') prevPlatformMean[platformId(k)] = p.mean_score;
  });

  // First-class questions (from the tenant's primer questions), matched to the
  // report's questions by id or normalized text — same card shape as Top answers.
  const normalizeQ = (s?: string) => (s ?? '').trim().toLowerCase();
  const firstClass = (primerQuestions ?? []).filter((q) => q.is_first_class);
  const firstClassIds = new Set(firstClass.map((q) => q.id));
  const firstClassTexts = new Set(firstClass.map((q) => normalizeQ(q.question)).filter(Boolean));
  const firstClassForPlatform = questionRows
    .filter(({ id, q }) => firstClassIds.has(id) || firstClassTexts.has(normalizeQ(q.question_text)))
    .map(({ id, q, score }) => ({ question_id: id, question_text: q.question_text, total_score: score, answer_preview: answerByQuestion[id] }))
    .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0));

  // Best score any platform achieved on a question — used for the "gap to best" sort.
  const bestScoreOf = (q: { platform_scores?: Record<string, number> }) => {
    const vals = Object.values(q.platform_scores ?? {});
    return vals.length ? Math.max(...vals) : 0;
  };
  const sortedQuestions = [...questionRows].sort((a, b) => {
    if (questionSort === 'highest') return (b.score ?? 0) - (a.score ?? 0);
    if (questionSort === 'az') return (a.q.question_text || a.id).localeCompare(b.q.question_text || b.id);
    if (questionSort === 'gap') return (bestScoreOf(b.q) - (b.score ?? 0)) - (bestScoreOf(a.q) - (a.score ?? 0));
    return (a.score ?? 0) - (b.score ?? 0); // lowest (weakest first)
  });
  const deepDiveSuggestions = [
    `Why is ${platformLabel} underperforming?`,
    `How do I improve my authority on ${platformLabel}?`,
    `What content gaps hurt me on ${platformLabel}?`,
  ];

  const hasPlatformData = !!pa || questionRows.length > 0 || flaggedForPlatform.length > 0;

  return (
    <div>
      <div className="space-y-6 mt-4 pb-36">
        {/* Back + header */}
        <div>
          <button onClick={backToDashboard} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors">
            <ArrowLeftIcon className="h-4 w-4" /> Back to AI Authority
          </button>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <PlatformIcon platform={platform} />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{platformLabel} · Deep Dive</h1>
              </div>
            </div>
            {reports.length > 1 && (
              <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                <ReportSelector reports={reports} current={currentReport} latestId={latestReportId} onSelect={goToReport} />
                {!isLatest && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">Older report</span>
                    <button onClick={() => latestReport && goToReport(latestReport)} className="font-semibold text-[#161463] hover:underline">View latest</button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* meta chips */}
          {(meta?.total_responses || meta?.questions_tested || meta?.platforms_tested) && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
              {meta?.total_responses !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100">{fmt(meta.total_responses, 0)} responses analyzed</span>}
              {meta?.questions_tested !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100">{fmt(meta.questions_tested, 0)} questions</span>}
              {meta?.platforms_tested !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100">{fmt(meta.platforms_tested, 0)} platforms compared</span>}
            </div>
          )}
        </div>

        <div key={reportId ?? 'default'} className="space-y-6 animate-fade-in-up">
          {!hasPlatformData && recommendations.length === 0 && (
            <div className="bg-white border border-gray-100 p-8 text-center text-sm text-gray-500 ">
              No deep-dive data available for {platformLabel} in this report.
            </div>
          )}

          {/* Hero — headline score, rank, benchmark */}
          {pa && (
            <div className="border border-gray-100 bg-white  overflow-hidden">
              <div className="h-1.5 w-full bg-gradient-to-r from-[#161463] to-[#5457A6]" />
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex flex-col justify-center">
                  <p className="text-sm text-gray-500">Overall score on {platformLabel}</p>
                  <div className="flex items-end gap-2 mt-1">
                    <span className={`text-5xl font-black ${scoreTone(pa.mean_score).text}`}>{fmt(pa.mean_score)}</span>
                    <span className="text-sm text-gray-400 mb-2">/ 100</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-700">{scoreLabel(pa.mean_score)}</span>
                    {vsOverall !== undefined && (
                      <span className="inline-flex items-center gap-1 text-gray-400 text-sm">·<DeltaBadge delta={vsOverall} /><span className="text-xs">vs all platforms</span></span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 md:border-l md:border-gray-100 md:pl-6">
                  <div className="h-11 w-11 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <TrophyIcon className="h-5 w-5 text-[#161463]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {rank > 0 ? `#${rank}` : '—'}
                      <span className="text-sm font-medium text-gray-400"> of {platformRanking.length || '—'}</span>
                    </p>
                    <p className="text-xs text-gray-500">Rank among platforms</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 md:border-l md:border-gray-100 md:pl-6">
                  <MiniStat label="Median" value={fmt(pa.median_score)} />
                  <MiniStat label="Flagged" value={pa.flagged_percentage !== undefined ? `${fmt(pa.flagged_percentage)}%` : fmt(pa.flagged_count, 0)} />
                  <MiniStat label="Consistency" value={fmt(pa.std)} hint="lower = steadier" />
                  <MiniStat label="Responses" value={fmt(pa.count, 0)} />
                </div>
              </div>
            </div>
          )}

          {/* Strengths & opportunity */}
          {(strongest || weakest) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {strongest && (
                <div className="border border-emerald-100 bg-emerald-50/50 p-5">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <SparklesIcon className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wide">Strongest area</span>
                  </div>
                  <p className="mt-2 text-lg font-bold text-gray-900">{strongest.label}</p>
                  <p className="text-sm text-gray-600">
                    Scoring {fmt(strongest.val)}
                    {strongest.delta !== undefined && strongest.delta > 0 ? `, ${fmt(strongest.delta)} above the overall average` : ''}. Where {platformLabel} represents you best.
                  </p>
                </div>
              )}
              {weakest && (
                <div className="border border-amber-100 bg-amber-50/50 p-5">
                  <div className="flex items-center gap-2 text-amber-700">
                    <TargetIcon className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-wide">Biggest opportunity</span>
                  </div>
                  <p className="mt-2 text-lg font-bold text-gray-900">{weakest.label}</p>
                  <p className="text-sm text-gray-600">
                    Scoring {fmt(weakest.val)}
                    {weakest.delta !== undefined && weakest.delta < 0 ? `, ${fmt(Math.abs(weakest.delta))} below the overall average` : ''}. Focus here to lift {platformLabel}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Question performance */}
          {questionRows.length > 0 && (
            <Section
              title="Question performance"
              subtitle={`${questionRows.length} questions · tap a row to view the question & answer`}
              icon={TargetIcon}
              right={
                <div className="flex items-center gap-2 flex-wrap">
                  {belowCount > 0 && <span className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">{belowCount} below 60</span>}
                  <Listbox value={questionSort} onChange={setQuestionSort}>
                    <div className="relative">
                      <Listbox.Button className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#161463] focus:border-[#161463] transition">
                        <span className="font-medium">{QUESTION_SORTS.find((s) => s.value === questionSort)?.label}</span>
                        <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                      </Listbox.Button>
                      <Listbox.Options className="absolute right-0 z-30 mt-2 w-52 overflow-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
                        {QUESTION_SORTS.map((s) => (
                          <Listbox.Option key={s.value} value={s.value} className={({ active }) => `cursor-pointer rounded-lg px-3 py-2 text-sm ${active ? 'bg-gray-50' : ''}`}>
                            {({ selected }) => (
                              <div className="flex items-center justify-between gap-3">
                                <span className={selected ? 'font-semibold text-[#161463]' : 'text-gray-700'}>{s.label}</span>
                                {selected && <CheckIcon className="h-4 w-4 text-[#161463]" />}
                              </div>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </Listbox>
                </div>
              }
            >
              <ScrollBox scrollClassName="overflow-x-auto overflow-y-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-gray-200 text-left text-[#8181A5]">
                      <th className="py-2.5 pr-4 font-medium">Question</th>
                      <th className="py-2.5 pr-4 font-medium w-44">Score</th>
                      <th className="py-2.5 font-medium hidden sm:table-cell w-28">Standing</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sortedQuestions.map(({ id, q, score }) => {
                      const isBest = platformId(q.best_platform) === platformId(matchedKey);
                      const isWorst = platformId(q.worst_platform) === platformId(matchedKey);
                      const answerText = answerByQuestion[id];
                      return (
                        <tr
                          key={id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setModalQ({ id, questionText: q.question_text, score, answer: answerText })}
                        >
                          <td className="py-3 pr-4 text-gray-800 max-w-md">
                            <div className="flex items-center gap-1.5">
                              <Maximize2Icon className="h-3 w-3 text-gray-300 flex-shrink-0" />
                              <span className="line-clamp-2">{q.question_text || id}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-semibold w-9 ${scoreTone(score).text}`}>{fmt(score)}</span>
                              <div className="flex-1"><ScoreBar score={score} /></div>
                            </div>
                          </td>
                          <td className="py-3 hidden sm:table-cell">
                            {isBest ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircleIcon className="h-3 w-3" /> Top platform
                              </span>
                            ) : isWorst ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-600 border border-red-200">
                                <TrendingDownIcon className="h-3 w-3" /> Weakest
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollBox>
            </Section>
          )}

          {/* First class questions */}
          {firstClassForPlatform.length > 0 && (
            <Section title="First class questions" subtitle={`How ${platformLabel} answers your first-class questions`} icon={SparklesIcon} right={<span className="text-xs font-medium text-gray-400">{firstClassForPlatform.length}</span>}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {firstClassForPlatform.map((p, i) => (
                  <div key={p.question_id ?? i} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900">{p.question_text || p.question_id || 'Question'}</p>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {p.total_score !== undefined && <span className={`text-sm font-bold ${scoreTone(p.total_score).text}`}>{fmt(p.total_score)}</span>}
                        <DeltaBadge delta={questionDelta(p.question_id, p.total_score)} />
                      </div>
                    </div>
                    <AnswerBody text={p.answer_preview} />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Rubric performance + radar (side by side on large screens) */}
          {pa?.rubric_averages && (
            <div className={`grid grid-cols-1 gap-6 items-stretch ${radarPlatforms.length >= 2 ? 'lg:grid-cols-2' : ''}`}>
            <Section title="Rubric performance" subtitle={`How ${platformLabel} scores per dimension, against the all-platform average`} icon={TargetIcon}>
              <div className="space-y-4">
                {rubricRows.map((r) => (
                  <div key={String(r.key)}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-gray-700">{r.label}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${scoreTone(r.val).text}`}>{fmt(r.val)}</span>
                        <DeltaBadge delta={r.delta} />
                      </div>
                    </div>
                    <ScoreBar score={r.val} benchmark={r.bench} />
                  </div>
                ))}
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
                <span className="inline-block h-3 w-0.5 bg-gray-500/70 rounded" /> marker shows the overall average across all platforms
              </p>
            </Section>

            {radarPlatforms.length >= 2 && (
            <Section
              title="Rubric radar"
              subtitle={`${platformLabel} vs the other platforms — how each rubric stacks up`}
              icon={TargetIcon}
              right={
                <div className="relative group flex-shrink-0">
                  <HelpCircleIcon tabIndex={0} className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help outline-none" />
                  <div className="pointer-events-none absolute right-0 top-6 z-40 w-72 sm:w-80 rounded-lg border border-gray-200 bg-white p-3.5 shadow-xl text-xs text-gray-600 leading-relaxed opacity-0 invisible transition-opacity group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible">
                    <p className="font-semibold text-gray-800 mb-1">Reading the radar</p>
                    <p className="mb-2.5">Each spoke is a scoring dimension (0–100). {platformLabel}&apos;s shaded shape is its profile; the coloured outlines are the other platforms. A larger, more even shape means stronger, more balanced performance. Hover a spoke on the chart to compare every platform&apos;s score for that dimension.</p>
                    <p className="font-semibold text-gray-800 mb-1">The five dimensions</p>
                    <ul className="space-y-1">
                      <li><span className="font-medium text-gray-700">Visibility</span> — how accurately AI systems describe your brand.</li>
                      <li><span className="font-medium text-gray-700">Authority</span> — how often and prominently your brand appears in answers.</li>
                      <li><span className="font-medium text-gray-700">Conversion</span> — how strongly answers position your brand as the recommendation.</li>
                      <li><span className="font-medium text-gray-700">Competitive Share</span> — your share of mentions versus competitors.</li>
                      <li><span className="font-medium text-gray-700">Sentiment</span> — the tone and favorability of references to your brand.</li>
                    </ul>
                  </div>
                </div>
              }
            >
              <div className="h-80 sm:h-96 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="rubric" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tickCount={5} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} />
                    {orderedRadarPlatforms.map((k) => {
                      const isCurrent = platformId(k) === platformId(matchedKey);
                      const color = radarColorFor(k);
                      return (
                        <Radar
                          key={k}
                          name={canonicalPlatform(k)}
                          dataKey={k}
                          stroke={color}
                          strokeWidth={isCurrent ? 2.5 : 1.25}
                          fill={color}
                          fillOpacity={isCurrent ? 0.3 : 0}
                          dot={isCurrent ? { r: 3, fill: '#161463', stroke: '#fff', strokeWidth: 1.5 } : false}
                          isAnimationActive={false}
                        />
                      );
                    })}
                    <Tooltip content={renderRadarTooltip} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-1 text-center text-[11px] text-gray-400">Filled = {platformLabel} · outlines = other platforms</p>
            </Section>
            )}
            </div>
          )}

          {/* Platform leaderboard */}
          {platformRanking.length > 1 && (
            <Section title="Platform leaderboard" subtitle="Where each AI platform ranks by overall score" icon={TrophyIcon}>
              <div className="space-y-1.5">
                {platformRanking.map((e, i) => {
                  const isCurrent = e.name === matchedKey;
                  const display = canonicalPlatform(e.name);
                  return (
                    <div key={e.name} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 ${isCurrent ? 'bg-indigo-50 border border-indigo-100' : ''}`}>
                      <span className="w-5 text-sm font-bold text-gray-400 text-center">{i + 1}</span>
                      <PlatformIcon platform={display} />
                      <span className={`w-24 sm:w-32 truncate text-sm ${isCurrent ? 'font-bold text-[#161463]' : 'font-medium text-gray-700'}`}>{display}</span>
                      <div className="flex-1"><ScoreBar score={e.mean} /></div>
                      <span className={`w-10 text-right text-sm font-semibold ${scoreTone(e.mean).text}`}>{fmt(e.mean)}</span>
                      <span className="w-14 flex justify-end"><DeltaBadge delta={prevPlatformMean[platformId(e.name)] !== undefined ? e.mean - prevPlatformMean[platformId(e.name)] : undefined} /></span>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Score distribution */}
          {questionRows.length > 0 && (
            <Section title="Score distribution" subtitle={`How ${platformLabel}'s ${questionRows.length} question scores are spread`} icon={BarChart3Icon}>
              <div className="flex items-end justify-between gap-3 h-40">
                {distribution.map((count, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                    <span className="text-xs font-semibold text-gray-700 mb-1">{count}</span>
                    <div className={`w-full rounded-t ${DIST_COLORS[i]}`} style={{ height: `${(count / maxBucket) * 100}%`, minHeight: count > 0 ? '4px' : '0' }} />
                    <span className="mt-2 text-[11px] text-gray-500">{DIST_LABELS[i]}</span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Top answers */}
          {bestForPlatform.length > 0 && (
            <Section title="Top answers" subtitle={`Where ${platformLabel} performs best`} icon={SparklesIcon} right={<span className="text-xs font-medium text-gray-400">{bestForPlatform.length}</span>}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {bestForPlatform.map((p, i) => (
                  <div key={p.question_id ?? i} className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900">{p.question_text || p.question_id || 'Question'}</p>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {p.total_score !== undefined && <span className={`text-sm font-bold ${scoreTone(p.total_score).text}`}>{fmt(p.total_score)}</span>}
                        <DeltaBadge delta={questionDelta(p.question_id, p.total_score)} />
                      </div>
                    </div>
                    <AnswerBody text={p.answer_preview} />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Flagged answers */}
          {flaggedForPlatform.length > 0 && (
            <Section title="Flagged answers" subtitle={`Responses on ${platformLabel} that need a closer look`} icon={FlagIcon} right={<span className="text-xs font-medium text-gray-400">{flaggedForPlatform.length}</span>}>
              <ScrollBox scrollClassName={`pr-1 ${flaggedForPlatform.length > 9 ? 'max-h-[36rem] overflow-y-auto' : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {flaggedForPlatform.map((a, i) => (
                    <FlaggedAnswerCard key={a.question_id ?? i} answer={a} delta={questionDelta(a.question_id, a.total_score)} />
                  ))}
                </div>
              </ScrollBox>
            </Section>
          )}

          {/* Competitive landscape */}
          {cc && ((cc.competitors?.length ?? 0) > 0 || cc.brand_base_score !== undefined) && (
            <Section title="Competitive landscape" subtitle="How your brand stacks up against competitors" icon={UsersIcon}>
              <CompetitiveLandscape data={cc} />
            </Section>
          )}

          {/* Brand vs competitors, by question */}
          {brandVsCompetitors.length > 0 && (
            <Section
              title="Brand vs competitors, by question"
              subtitle={`How ${platformLabel} stacks up against your competitors on each question — biggest deficits first`}
              icon={UsersIcon}
              right={<span className="text-xs font-medium text-gray-400">{brandVsCompetitors.length}</span>}
            >
              <ScrollBox scrollClassName={`pr-1 ${brandVsCompetitors.length > 6 ? 'max-h-[36rem] overflow-y-auto' : ''}`}>
                <div className="space-y-3">
                  {brandVsCompetitors.map(({ id, question_text, score, comps, gap }) => (
                    <div key={id} className="rounded-xl border border-gray-100 p-3.5">
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <p className="text-sm font-medium text-gray-900">{question_text || id}</p>
                        {gap !== undefined && (
                          <span className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${gap >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                            {gap >= 0 ? `Leading by ${fmt(gap)}` : `Trailing by ${fmt(Math.abs(gap))}`}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <CompareRow label={`${platformLabel} (you)`} score={score} highlight />
                        {[...comps].sort((a, b) => b.score - a.score).map((c) => (
                          <CompareRow key={c.name} label={c.name} score={c.score} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollBox>
            </Section>
          )}

          {/* Recommendations */}
          {(recommendations.length > 0 || nextSteps?.static_guidance || (nextSteps?.actions?.length ?? 0) > 0) && (
            <Section title="Recommendations" subtitle="Prioritized ways to strengthen your AI authority" icon={LightbulbIcon}>
              {recommendations.length > 0 && (
                <div className="space-y-3">
                  {recommendations.map((rec, i) => {
                    const tone = priorityMeta(rec.priority);
                    return (
                      <div key={i} className="rounded-xl border border-gray-100 p-4 hover: transition-shadow">
                        <div className="flex items-start justify-between gap-3">
                          {rec.category && <span className="text-xs font-semibold uppercase tracking-wide text-[#161463]">{rec.category}</span>}
                          {rec.priority && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${tone.badge}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />{rec.priority}
                            </span>
                          )}
                        </div>
                        {rec.issue && <p className="mt-1.5 text-sm text-gray-500"><span className="font-medium text-gray-700">Issue:</span> {mapNames(rec.issue)}</p>}
                        {rec.recommendation && <p className="mt-1.5 text-sm text-gray-800">{mapNames(rec.recommendation)}</p>}
                      </div>
                    );
                  })}
                </div>
              )}

              {(nextSteps?.actions?.length ?? 0) > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Next steps</p>
                  <ul className="space-y-2">
                    {nextSteps!.actions!.map((a, i) => {
                      const tone = priorityMeta(a.priority);
                      return (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${tone.dot}`} />
                          <span>{mapNames(a.action)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {nextSteps?.static_guidance && <p className="mt-4 text-sm text-gray-500 italic border-t border-gray-100 pt-4">{mapNames(nextSteps.static_guidance)}</p>}
            </Section>
          )}
        </div>

      </div>

      {/* Dashboard assistant composer — fixed bottom center */}
      <DashboardAssistantBar
        onSubmit={handleAssistantSubmit}
        onOpen={() => setShowAIChat(true)}
        suggestions={deepDiveSuggestions}
      />

      {/* AI Insights Drawer — ai_authority context */}
      <AIInsightsDrawer
        isOpen={showAIChat}
        onClose={() => { setShowAIChat(false); setInitialAIMessage(undefined); }}
        contextKey="ai_authority"
        description="AI Authority context"
        title={`Ask me anything about ${platformLabel}`}
        subtitle="Get insights grounded in your latest LARS report and your own content."
        suggestions={deepDiveSuggestions}
        initialMessage={initialAIMessage}
      />

      {/* Question detail modal */}
      {modalQ && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setModalQ(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Question</p>
                <h3 className="text-base font-semibold text-gray-900 mt-1">{modalQ.questionText || modalQ.id}</h3>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">{platformLabel} score</span>
                  <span className={`text-sm font-bold ${scoreTone(modalQ.score).text}`}>{fmt(modalQ.score)}</span>
                </div>
              </div>
              <button onClick={() => setModalQ(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0" title="Close">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{platformLabel}&apos;s answer</p>
              {modalQ.answer ? (
                <MarkdownRenderer content={modalQ.answer} className="text-sm text-gray-700" />
              ) : (
                <p className="text-sm text-gray-400 italic">No answer returned by {platformLabel} for this question.</p>
              )}
            </div>

            {(cc?.competitors?.length ?? 0) > 0 && (
              <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50">
                <p className="text-xs font-medium text-gray-700 mb-2">Compare against competitors on this question</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {cc!.competitors!.map((c) => {
                    const name = c.name || 'Competitor';
                    const selected = selectedCompetitors.has(name);
                    return (
                      <button
                        key={name}
                        onClick={() => toggleCompetitor(name)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-[#161463] text-white border-[#161463]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      const names = (cc?.competitors ?? []).map((c) => c.name || 'Competitor');
                      const chosen = names.filter((n) => selectedCompetitors.has(n));
                      const list = (chosen.length ? chosen : names).join(', ');
                      const qtext = modalQ.questionText || modalQ.id;
                      handleAssistantSubmit(`On ${platformLabel}, compare how my brand performs versus ${list} for this question: "${qtext}". Where do these competitors do better, and what should I change to win this query?`);
                      setModalQ(null);
                    }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-4 py-2 text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <SparklesIcon className="h-4 w-4" /> Ask AI to compare{selectedCompetitors.size > 0 ? ` (${selectedCompetitors.size})` : ''}
                  </button>
                  <span className="text-[11px] text-gray-400">Pick competitors to focus on, or leave blank to compare against all.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformDeepDive;
